/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import * as yup from 'yup';

import {
  AMAZON_CONNECT_BYOSD_TYPE,
  NICE_INCONTACT_BYOSD_TYPE
} from '../../util/channelTypes';
import {
  NO_ONLY_SPACES_REGEX,
  PHONE_REGEX
} from '../../util/validation/constants';
import { AdvancedOptions } from './advanced-tab/messages';
import { LiveAgent } from './connections-tab/messages';
import {
  BROADBAND,
  DEFAULT_VOICE_PITCH,
  DEFAULT_VOICE_SPEED,
  MULTIMEDIA,
  NARROWBAND,
  TELEPHONY
} from './constants';
import { Schema } from './messages';

export const StreamProvider = {
  GENESYS_AUDIO_CONNECTOR: 'genesys_audio_connector'
};

export const JWT = 'jwt';

export const FIELDS = {
  PHONE_NUMBERS: 'phone_numbers',
  FIRST_PHONE_NUMBER: 'phone_numbers.0',
  FIRST_PHONE_NUMBER_AUTO_PROVISIONED: 'phone_numbers.0.auto_provisioned',
  FIRST_PHONE_NUMBER_PROVISION_SUCCESS: 'phone_numbers.0.provision_success',
  PHONE_NUMBER_NAME: (index: number) => `phone_numbers.${index}.name`,
  PHONE_NUMBER_NUMBER: (index: number) => `phone_numbers.${index}.phone_number`,
  TRANSFER_FAILURE_AGENT: 'private.secrets.transfer_failure_agent',
  ADVANCED_CUSTOM_EXTN_ASYNC_CALLOUT: 'async_callout',
  SIP: {
    AUTHENTICATION: 'session_initiation_protocol.security.authentication',
    SECURE_TRUNKING: 'session_initiation_protocol.security.secure_trunking',
    USERNAME: 'private.sip_authentication_username',
    PASSWORD: 'private.secrets.sip_authentication_password',
    CUSTOM_INVITE_HEADERS: 'session_initiation_protocol.custom_invite_headers',
    PUT_CALLER_ON_HOLD_ON_TRANSFER:
      'session_initiation_protocol.put_caller_on_hold_on_transfer',
    SEND_PROVISIONAL_RESPONSE:
      'session_initiation_protocol.send_provisional_response'
  },
  STT: {
    APIKEY: 'private.secrets.speech_to_text_apikey',
    BEARER_TOKEN: 'private.secrets.speech_to_text_bearer_token',
    INSTANCE_ID: 'speech_to_text.instance_id',
    URL: 'speech_to_text.url',
    BAND_PREFERENCE: 'speech_to_text.band_preference',
    NARROWBAND: {
      MODEL: 'speech_to_text.config.model',
      ACCOUSTIC_CUSTOMIZATION_ID:
        'speech_to_text.config.accoustic_customization_id',
      LANGUAGE_CUSTOMIZATION_ID:
        'speech_to_text.config.language_customization_id'
    },
    BROADBAND: {
      MODEL: 'speech_to_text.broadband_config.model',
      ACCOUSTIC_CUSTOMIZATION_ID:
        'speech_to_text.broadband_config.accoustic_customization_id',
      LANGUAGE_CUSTOMIZATION_ID:
        'speech_to_text.broadband_config.language_customization_id'
    },
    LANGUAGE_SELECTIONS_FILTER_TOGGLE: 'speech_to_text.language_filter_toggle'
  },
  TTS: {
    APIKEY: 'private.secrets.text_to_speech_apikey',
    ELEVENLABS_APIKEY: 'private.secrets.eleven_labs_apikey',
    BEARER_TOKEN: 'private.secrets.text_to_speech_bearer_token',
    INSTANCE_ID: 'text_to_speech.instance_id',
    URL: 'text_to_speech.url',
    PROVIDER: 'text_to_speech.provider',
    CUSTOMIZATION_ID: 'text_to_speech.config.customization_id',
    VOICE: 'text_to_speech.config.voice',
    VOICE_ID: 'text_to_speech.config.voice_id',
    MODEL_ID: 'text_to_speech.config.model_id',
    PITCH_PERCENTAGE: 'text_to_speech.config.pitch_percentage',
    RATE_PERCENTAGE: 'text_to_speech.config.rate_percentage',
    ELEVENLABS_VOICE_SETTINGS_CONFIG: 'text_to_speech.config.voice_settings',
    ELEVENLABS_VOICE_SETTINGS: {
      STABILITY: 'text_to_speech.config.voice_settings.stability',
      SIMILARITY_BOOST: 'text_to_speech.config.voice_settings.similarity_boost',
      STYLE: 'text_to_speech.config.voice_settings.style',
      USE_SPEAKER_BOOST:
        'text_to_speech.config.voice_settings.use_speaker_boost',
      SPEED: 'text_to_speech.config.voice_settings.speed'
    }
  },
  ERRORS: {
    CALL: {
      REPLY_MESSAGE: 'error_handling.call_failure.reply_message',
      REPLY_URL: 'error_handling.call_failure.reply_url'
    },
    TRANSFER: {
      DISCONNECT_CALL: 'error_handling.transfer_failure.disconnect_call',
      REPLY_MESSAGE: 'error_handling.transfer_failure.reply_message',
      REPLY_URL: 'error_handling.transfer_failure.reply_url'
    }
  },
  CONTACT_CENTER: {
    ACCESS_KEY_ID: 'private.secrets.contact_center.access_key_id',
    ACCESS_KEY_SECRET: 'private.secrets.contact_center.access_key_secret',
    NICE_CXONE_CONTACT_ID:
      'contact_center.config.session_initiation_protocol.invite_headers.nice_cxone_contact_id',
    ARN: 'contact_center.config.arn',
    PROVIDER: 'contact_center.provider',
    ROOT: 'contact_center',
    ROOT_SECRETS: 'private.secrets.contact_center'
  },
  SOE: {
    ENABLED: 'service_orchestration_engine.enabled',
    MESSAGING_VERSION:
      'service_orchestration_engine.watson_assistant_messaging_version',
    URL: 'service_orchestration_engine.url',
    USERNAME: 'private.secrets.soe_username',
    PASSWORD: 'private.secrets.soe_password'
  },
  GENESYS_AUDIO_CONNECTOR: {
    API_KEY: 'private.authorization.provider_authorization.api_key',
    PROVIDER_AUTHORIZATION:
      'private.authorization.provider_authorization.provider',
    AUTHORIZATION_TYPE: 'private.authorization.type',
    CLIENT_SECRET: 'private.secrets.provider_secrets.client_secret',
    CONNECT_URL: 'connect_url'
  }
};

export const phoneSettingsSchema = yup.object().shape({
  private: yup.object().shape({
    secrets: yup
      .object()
      .shape({
        sip_authentication_password: yup
          .string()
          .test(
            'sip-auth-password-required',
            Schema.sipAuthPasswordRequired.defaultMessage,
            (value, context: yup.TestContext) => {
              const contextGeneric = context as unknown as {
                from: {
                  value: {
                    session_initiation_protocol: {
                      security: { authentication: string };
                    };
                  };
                }[];
              };
              // The from[2] is grabbing the schema context from the root level
              // by grabbing the 2nd parent from where we currently are in the schema
              const phoneSettingsSchemaRoot = contextGeneric.from[2].value;
              const authenticationEnabled =
                phoneSettingsSchemaRoot?.session_initiation_protocol?.security
                  ?.authentication;
              return authenticationEnabled ? !_.isEmpty(value) : true;
            }
          ),
        voice_gateway_origin_key: yup.string().default(undefined),
        speech_to_text_apikey: yup.string().default(undefined),
        speech_to_text_billing_access_token: yup.string().default(undefined),
        text_to_speech_apikey: yup.string().default(undefined),
        text_to_speech_billing_access_token: yup.string().default(undefined),
        transfer_failure_agent: yup.string().default(undefined),
        contact_center: yup
          .object()
          .shape({
            access_key_id: yup
              .string()
              .default('')
              .nullable()
              .test(
                'access-key-id-required',
                Schema.contactCenterAccessKeyIdRequired.defaultMessage,
                (value, context: yup.TestContext) => {
                  const contextGeneric = context as unknown as {
                    from: { value: { contact_center: { provider: string } } }[];
                  };
                  // The from[3] is grabbing the schema context from the root level
                  // by grabbing the 3rd parent from where we currently are in the schema
                  const phoneSettingsSchemaRoot = contextGeneric.from[3].value;
                  const provider =
                    phoneSettingsSchemaRoot?.contact_center?.provider;
                  const requiredByProvider =
                    provider === NICE_INCONTACT_BYOSD_TYPE ||
                    provider === AMAZON_CONNECT_BYOSD_TYPE;
                  return requiredByProvider ? !_.isEmpty(value) : true;
                }
              ),
            access_key_secret: yup
              .string()
              .default('')
              .nullable()
              .test(
                'access-key-secret-required',
                Schema.contactCenterAccessKeySecretRequired.defaultMessage,
                (value, context: yup.TestContext) => {
                  const contextGeneric = context as unknown as {
                    from: { value: { contact_center: { provider: string } } }[];
                  };
                  // The from[3] is grabbing the schema context from the root level
                  // by grabbing the 3rd parent from where we currently are in the schema
                  const phoneSettingsSchemaRoot = contextGeneric.from[3].value;
                  const provider =
                    phoneSettingsSchemaRoot?.contact_center?.provider;
                  const requiredByProvider =
                    provider === NICE_INCONTACT_BYOSD_TYPE ||
                    provider === AMAZON_CONNECT_BYOSD_TYPE;
                  return requiredByProvider ? !_.isEmpty(value) : true;
                }
              )
          })
          .nullable()
          .default(undefined)
      })
      .nullable()
      .default(undefined),
    sip_authentication_username: yup
      .string()
      .test(
        'sip-auth-username-required',
        Schema.sipAuthUsernameRequired.defaultMessage,
        (value, context: yup.TestContext) => {
          const contextGeneric = context as unknown as {
            from: {
              value: {
                session_initiation_protocol: {
                  security: { authentication: string };
                };
              };
            }[];
          };
          // The from[1] is grabbing the schema context from the root level
          // by grabbing the 1st parent from where we currently are in the schema
          const phoneSettingsSchemaRoot = contextGeneric.from[1].value;
          const authenticationEnabled =
            phoneSettingsSchemaRoot?.session_initiation_protocol?.security
              ?.authentication;
          return authenticationEnabled ? !_.isEmpty(value) : true;
        }
      )
  }),
  async_callout: yup.boolean().default(true),
  contact_center: yup.object().shape({
    provider: yup.string().nullable().default(''),
    config: yup
      .object()
      .when(Schema.providerPathFromContactCenterConfig.defaultMessage, {
        is: NICE_INCONTACT_BYOSD_TYPE,
        then: yup.object().shape({
          session_initiation_protocol: yup.object().shape({
            invite_headers: yup.object().shape({
              nice_cxone_contact_id: yup
                .string()
                .nullable()
                .default('')
                .required(Schema.contactCenterIdRequired)
            })
          })
        })
      })
      .when(Schema.providerPathFromContactCenterConfig.defaultMessage, {
        is: AMAZON_CONNECT_BYOSD_TYPE,
        then: yup.object().shape({
          arn: yup
            .string()
            .nullable()
            .default('')
            .required(Schema.contactCenterArnRequired.defaultMessage)
            .matches(/^(arn:aws:connect:).+:.+:.+\/.+/, {
              message: Schema.contactCenterArnRequired.defaultMessage,
              excludeEmptyString: false
            })
        })
      })
  }),
  session_initiation_protocol: yup.object().shape({
    put_caller_on_hold_on_transfer: yup.boolean().default(true),
    send_provisional_response: yup.boolean().default(true),
    custom_invite_headers: yup.array().default([]),
    custom_session_header: yup.string().nullable().default(undefined),
    security: yup.object().shape({
      authentication: yup.boolean().default(false),
      secure_trunking: yup.boolean().default(false)
    })
  }),
  phone_numbers: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string(),
        name: yup.string().max(128, Schema.phoneDescriptionInvalid).default(''),
        primary: yup.boolean(),
        auto_provisioned: yup.boolean(),
        phone_number: yup
          .string()
          .matches(PHONE_REGEX, {
            message: Schema.phoneNumberInvalid,
            excludeEmptyString: true
          })
          .matches(NO_ONLY_SPACES_REGEX, {
            message: Schema.phoneNumberInvalid,
            excludeEmptyString: true
          })
          .test(
            'len',
            Schema.phoneNumberInvalid,
            val =>
              val.toString().replace(/^\D+/g, '').length >= 4 &&
              val.toString().replace(/^\D+/g, '').length <= 15
          )
          .default('')
      })
    )
    .default([]),
  assistant_notifications: yup
    .array()
    .of(
      yup.object().shape({
        name: yup.string().default(''),
        enabled: yup.boolean().default(false)
      })
    )
    .default([
      {
        name: 'network_events',
        enabled: false
      },
      {
        name: 'filtered_speech_utterances',
        enabled: false
      },
      {
        name: 'media_change',
        enabled: false
      }
    ]),
  error_handling: yup.object().shape({
    call_failure: yup.object().shape({
      reply_message: yup
        .string()
        .max(150, LiveAgent.messageTooLong)
        .default(LiveAgent.callFailureDefault.defaultMessage),
      reply_url: yup
        .string()
        .default(LiveAgent.callFailureReplyURL.defaultMessage)
    }),
    transfer_failure: yup.object().shape({
      disconnect_call: yup.boolean().default(true),
      reply_message: yup
        .string()
        .max(150, LiveAgent.messageTooLong)
        .default(LiveAgent.transferFailureDefault.defaultMessage),
      reply_url: yup
        .string()
        .default(LiveAgent.errorHandlingReplyURL.defaultMessage)
    })
  }),
  speech_to_text: yup.object().shape({
    instance_id: yup
      .string()
      .nullable()
      .default(undefined)
      .ensure()
      .required(Schema.sttInstanceRequired),
    url: yup.string().nullable().default(undefined),
    band_preference: yup.string().default(TELEPHONY),
    config: yup.object().shape({
      accoustic_customization_id: yup.string().nullable(),
      language_customization_id: yup.string().nullable(),
      model: yup
        .string()
        .ensure()
        .test(
          'speechToTextConfigValidation',
          Schema.sttInstanceRequired,
          (value, context: yup.TestContext) => {
            const contextGeneric = context as unknown as {
              from: { value: Record<string, unknown> }[];
            };
            // The from[1] is grabbing the schema context from the speech_to_text level
            // by grabbing the 1st parent from where we currently are in the schema
            const speechToTextSchema = contextGeneric?.from[1].value;
            const bandPreference = speechToTextSchema?.band_preference;
            return bandPreference === TELEPHONY || bandPreference === NARROWBAND
              ? // `isNil` is used here instead of `isEmpty` because
                // the empty string is used to depict that no model
                // is selected
                !_.isNil(value)
              : true;
          }
        )
    }),
    broadband_config: yup.object().shape({
      accoustic_customization_id: yup.string().nullable(),
      language_customization_id: yup.string().nullable(),
      model: yup
        .string()
        .ensure()
        .test(
          'speechToTextConfigValidation',
          Schema.sttInstanceRequired,
          (value, context: yup.TestContext) => {
            const contextGeneric = context as unknown as {
              from: { value: Record<string, unknown> }[];
            };
            // The from[1] is grabbing the schema context from the speech_to_text level
            // by grabbing the 1st parent from where we currently are in the schema
            const speechToTextSchema = contextGeneric?.from[1].value;
            const bandPreference = speechToTextSchema?.band_preference;
            return bandPreference === MULTIMEDIA || bandPreference === BROADBAND
              ? // `isNil` is used here instead of `isEmpty` because
                // the empty string is used to depict that no model
                // is selected
                !_.isNil(value)
              : true;
          }
        )
    })
  }),
  text_to_speech: yup.object().shape({
    instance_id: yup
      .string()
      .nullable()
      .default(undefined)
      .ensure()
      .required(Schema.ttsInstanceRequired),
    url: yup.string().nullable().default(undefined),
    config: yup.object().shape({
      customization_id: yup.string().nullable(),
      pitch_percentage: yup
        .string()
        .ensure()
        .default(DEFAULT_VOICE_PITCH.toString()),
      rate_percentage: yup
        .string()
        .ensure()
        .default(DEFAULT_VOICE_SPEED.toString()),
      voice: yup.string().ensure()
    })
  })
});

export const phoneSoeSettingsSchema = phoneSettingsSchema.concat(
  yup.object().shape({
    private: yup.object().shape({
      secrets: yup.object().shape({
        soe_username: yup.string(),
        soe_password: yup.string()
      })
    }),
    service_orchestration_engine: yup.object().shape({
      enabled: yup.boolean().default(false),
      watson_assistant_messaging_version: yup.string().required().default('v1'),
      url: yup
        .string()
        .default('')
        .when('enabled', {
          is: true,
          then: yup
            .string()
            .url(AdvancedOptions.soeUrlRequired)
            .required(AdvancedOptions.soeUrlRequired)
        })
    })
  })
);

export const phoneGenesysAudioConnectorSchema = yup.object().shape({
  private: yup.object().shape({
    secrets: yup
      .object()
      .shape({
        provider_secrets: yup.object().shape({
          provider: yup
            .string()
            .nullable()
            .default(StreamProvider.GENESYS_AUDIO_CONNECTOR)
            .required(),
          client_secret: yup
            .string()
            .nullable()
            .default(undefined)
            .required(
              Schema.genesysAudioConnectorClientSecretRequired.defaultMessage
            )
        }),
        speech_to_text_apikey: yup.string().default(undefined),
        text_to_speech_apikey: yup.string().default(undefined),
        eleven_labs_apikey: yup.string().default(undefined)
      })
      .nullable()
      .default(undefined),
    authorization: yup.object().shape({
      type: yup.string().oneOf([JWT]).default(undefined).required(),
      provider_authorization: yup.object().shape({
        api_key: yup
          .string()
          .nullable()
          .default(undefined)
          .required(Schema.genesysAudioConnectorAPIKeyRequired.defaultMessage),
        provider: yup.string().nullable().default(undefined).required()
      })
    })
  }),
  speech_to_text: yup.object().shape({
    instance_id: yup
      .string()
      .nullable()
      .default(undefined)
      .ensure()
      .required(Schema.sttInstanceRequired),
    url: yup.string().nullable().default(undefined),
    config: yup.object().shape({
      accoustic_customization_id: yup.string().nullable(),
      language_customization_id: yup.string().nullable(),
      model: yup
        .string()
        .ensure()
        .test(
          'speechToTextConfigValidation',
          Schema.sttInstanceRequired,
          (value, context: yup.TestContext) => {
            const contextGeneric = context as unknown as {
              from: { value: Record<string, unknown> }[];
            };
            // The from[1] is grabbing the schema context from the speech_to_text level
            // by grabbing the 1st parent from where we currently are in the schema
            const speechToTextSchema = contextGeneric?.from[1].value;
            const bandPreference = speechToTextSchema?.band_preference;
            return bandPreference === TELEPHONY || bandPreference === NARROWBAND
              ? // `isNil` is used here instead of `isEmpty` because
                // the empty string is used to depict that no model
                // is selected
                !_.isNil(value)
              : true;
          }
        )
    })
  }),
  text_to_speech: yup.object().shape({
    provider: yup.string().default('watson_tts'),
    instance_id: yup
      .string()
      .nullable()
      .default(undefined)
      .ensure()
      .required(Schema.ttsInstanceRequired),
    url: yup.string().nullable().default(undefined),
    config: yup.object().when('provider', {
      is: 'eleven_labs',
      then: () =>
        yup.object().shape({
          voice: yup.string().required(),
          model_id: yup.string().required(),
          voice_settings: yup.object().shape({
            stability: yup.number(),
            similarity_boost: yup.number(),
            style: yup.number(),
            use_speaker_boost: yup.boolean().default(true),
            speed: yup.number()
          })
        }),
      otherwise: () =>
        yup.object().shape({
          customization_id: yup.string().nullable(),
          voice: yup.string().ensure(),
          pitch_percentage: yup
            .string()
            .ensure()
            .default(DEFAULT_VOICE_PITCH.toString()),
          rate_percentage: yup
            .string()
            .ensure()
            .default(DEFAULT_VOICE_SPEED.toString())
        })
    })
  }),
  async_callout: yup.boolean().default(true),
  connect_url: yup
    .string()
    .required(Schema.genesysAudioConnectorConnectURIRequired.defaultMessage)
});

// schema is slightly different in CPD for GenesysAudioConnector
export const phoneGenesysAudioConnectorSchemaInCpd = yup.object().shape({
  private: yup.object().shape({
    secrets: yup
      .object()
      .shape({
        provider_secrets: yup.object().shape({
          provider: yup
            .string()
            .nullable()
            .default(StreamProvider.GENESYS_AUDIO_CONNECTOR),
          client_secret: yup.string().nullable().default(undefined)
        }),
        speech_to_text_bearer_token: yup.string().default(undefined),
        text_to_speech_bearer_token: yup.string().default(undefined)
      })
      .nullable()
      .default(undefined),
    authorization: yup.object().shape({
      type: yup.string().oneOf([JWT]).default(undefined),
      provider_authorization: yup.object().shape({
        api_key: yup.string().nullable().default(undefined),
        provider: yup.string().nullable().default(undefined)
      })
    })
  }),
  speech_to_text: yup.object().shape({
    auth_type: yup.string().default('bearer'),
    instance_id: yup.string().nullable().default(undefined).ensure(),
    url: yup.string().nullable().default(undefined),
    config: yup.object().shape({
      accoustic_customization_id: yup.string().nullable(),
      language_customization_id: yup.string().nullable(),
      model: yup
        .string()
        .ensure()
        .test(
          'speechToTextConfigValidation',
          Schema.sttInstanceRequired,
          (value, context: yup.TestContext) => {
            const contextGeneric = context as unknown as {
              from: { value: Record<string, unknown> }[];
            };
            // The from[1] is grabbing the schema context from the speech_to_text level
            // by grabbing the 1st parent from where we currently are in the schema
            const speechToTextSchema = contextGeneric?.from[1].value;
            const bandPreference = speechToTextSchema?.band_preference;
            return bandPreference === TELEPHONY || bandPreference === NARROWBAND
              ? // `isNil` is used here instead of `isEmpty` because
                // the empty string is used to depict that no model
                // is selected
                !_.isNil(value)
              : true;
          }
        )
    })
  }),
  text_to_speech: yup.object().shape({
    auth_type: yup.string().default('bearer'),
    instance_id: yup.string().nullable().default(undefined).ensure(),
    url: yup.string().nullable().default(undefined),
    config: yup.object().shape({
      customization_id: yup.string().nullable(),
      pitch_percentage: yup
        .string()
        .ensure()
        .default(DEFAULT_VOICE_PITCH.toString()),
      rate_percentage: yup
        .string()
        .ensure()
        .default(DEFAULT_VOICE_SPEED.toString()),
      voice: yup.string().ensure()
    })
  }),
  async_callout: yup.boolean().default(true),
  connect_url: yup.string()
});
